/**
 * Context-menu commands for node-graph nodes: copy the underlying TSL node as
 * JSON (safe-stringified) and log it to the browser console. Especially handy
 * for the opaque "Node" cards we can't fully introspect statically.
 *
 * The card tags its underlying node as a context-menu target (see
 * GraphNodeCard); these commands pull it out of the {@link ContextMenuContext}.
 */

import { CommandsRegistry } from "../../commands/common/commands.js";
import { MenuId, MenuRegistry } from "../../actions/common/actions.js";
import { ContextKeyExpr } from "../../contextkey/common/contextkey.js";
import { ContextMenuContext, isContextMenuContext } from "../../contextview/common/context.js";
import { INodeGraphInspectionService } from "./nodeGraphInspectionService.js";
import { IClipboardService } from "../../clipboard/common/clipboardService.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { INotificationService } from "../../notifications/common/notifications.js";
import { IOpenerService } from "../../opener/common/opener.js";
import { safeStringify } from "../../../base/common/objects.js";
import { isNodeLike } from "./helper.nodes.js";

export const CopyNodeJsonCommandId = "nodegraph.node.copyJson";
export const LogNodeCommandId = "nodegraph.node.log";
export const OpenNodeDocsCommandId = "nodegraph.node.docs";
export const OpenShaderGraphCommandId = "nodegraph.node.openShaderGraph";

/**
 * The best payload behind the clicked graph node. The card passes a tagged
 * target carrying everything it knows; resolve in order of usefulness:
 * the raw TSL node → the live rendergraph resource (deref, falling back to its
 * snapshot after GC) → the node's meta (e.g. a pass node's order/depth).
 */
type TaggedNodeTarget = {
    /** The node card's header label, e.g. "Render Call" or "mix". */
    title?: unknown;
    node?: unknown;
    resource?: { deref?: () => object | undefined };
    meta?: unknown;
    docsUrl?: unknown;
};

/** The tagged target the graph card attaches to its context menu, if any. */
function findTagged(args: unknown): TaggedNodeTarget | undefined {
    if (!isContextMenuContext(args)) return undefined;
    const targets = (args as ContextMenuContext).targets ?? [];
    return targets.find(
        (t) => !!t && typeof t === "object" && (t as { nodegraphNode?: boolean }).nodegraphNode === true,
    ) as TaggedNodeTarget | undefined;
}

function nodeTarget(args: unknown): object | null {
    if (!isContextMenuContext(args)) return null;
    const targets = (args as ContextMenuContext).targets ?? [];
    const tagged = findTagged(args);
    if (tagged) {
        if (isNodeLike(tagged.node)) return tagged.node;
        const resource = tagged.resource;
        if (resource && typeof resource.deref === "function") return resource.deref() ?? (resource as object);
        if (tagged.meta && typeof tagged.meta === "object") return tagged.meta as object;
        return null;
    }
    // Back-compat: a raw TSL node passed directly in the targets.
    const target = targets.find((t) => isNodeLike(t));
    return (target as object) ?? null;
}

/**
 * Deep-copies a node's own enumerable properties into plain objects. Three.js
 * nodes define `toJSON()` (returning a `{ uuid, type, metadata }` stub), which
 * `JSON.stringify` would honor — so we build plain objects to bypass it, drop
 * functions, guard cycles, and cap depth to keep the dump reasonable.
 */
function nodeToPlain(value: unknown, seen = new WeakSet<object>(), depth = 0): unknown {
    if (value === null || typeof value !== "object") {
        return typeof value === "function" ? undefined : value;
    }
    if (seen.has(value)) return "[Circular]";
    if (depth > 8) return "[…]";
    seen.add(value);
    if (Array.isArray(value)) return value.map((v) => nodeToPlain(v, seen, depth + 1));
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
        const v = (value as Record<string, unknown>)[key];
        if (typeof v === "function") continue;
        out[key] = nodeToPlain(v, seen, depth + 1);
    }
    return out;
}

CommandsRegistry.registerCommand(CopyNodeJsonCommandId, (accessor, args) => {
    const node = nodeTarget(args);
    if (!node) return;
    accessor.getIfExists(IAnalyticsService)?.logEvent("nodegraph.node.copy-json", {});
    accessor.get(IClipboardService).writeText(safeStringify(nodeToPlain(node), 2));
});

CommandsRegistry.registerCommand(LogNodeCommandId, (accessor, args) => {
    const node = nodeTarget(args);
    if (!node) return;
    accessor.getIfExists(IAnalyticsService)?.logEvent("nodegraph.node.log", {});
    const title = findTagged(args)?.title;
    console.log(`[nodegraph] ${typeof title === "string" && title ? title : "node"}:`, node);
});

CommandsRegistry.registerCommand(OpenNodeDocsCommandId, (accessor, args) => {
    const tagged = findTagged(args);
    const docsUrl = tagged?.docsUrl;
    if (typeof docsUrl !== "string" || !docsUrl) {
        // Track the misses too — which nodes people WANT docs for.
        const meta = tagged?.meta as { nodeType?: unknown } | undefined;
        accessor.getIfExists(IAnalyticsService)?.logEvent("nodegraph.node.docs", {
            available: false,
            nodeType: typeof meta?.nodeType === "string" ? meta.nodeType : undefined,
        });
        accessor.getIfExists(INotificationService)?.showNotification({
            message: "No documentation available for this node.",
            type: "info",
            position: "cursor",
        });
        return;
    }
    accessor.getIfExists(IAnalyticsService)?.logEvent("nodegraph.node.docs", { available: true, url: docsUrl });
    accessor.get(IOpenerService).open(docsUrl, { openExternal: true });
});

// Drill from a material's dependency node into its TSL shader graph — the panel
// owns the drill-down stack + the Pro intent-gate, so the command just asks it to
// step in (via INodeGraphInspectionService.requestStepInto). Shown only on
// material nodes that HAVE a shader graph (the context key set on menu open).
CommandsRegistry.registerCommand(OpenShaderGraphCommandId, (accessor, args) => {
    const meta = findTagged(args)?.meta;
    if (!meta || typeof meta !== "object") return;
    accessor.getIfExists(IAnalyticsService)?.logEvent("nodegraph.node.open-shader", {});
    accessor.getIfExists(INodeGraphInspectionService)?.requestStepInto(meta as Record<string, unknown>);
});

MenuRegistry.appendMenuItem(MenuId.NodeGraphNodeContext, {
    command: { id: OpenShaderGraphCommandId, title: "Open shader graph" },
    when: ContextKeyExpr.has("context.nodegraphNodeHasShader"),
    order: 5,
});
MenuRegistry.appendMenuItem(MenuId.NodeGraphNodeContext, {
    command: { id: CopyNodeJsonCommandId, title: "Copy node as JSON", icon: { id: "copy" } },
    order: 10,
});
MenuRegistry.appendMenuItem(MenuId.NodeGraphNodeContext, {
    command: { id: LogNodeCommandId, title: "Log node to console" },
    order: 20,
});
MenuRegistry.appendMenuItem(MenuId.NodeGraphNodeContext, {
    command: { id: OpenNodeDocsCommandId, title: "Open documentation", icon: { id: "icon-docs" } },
    order: 30,
});
